<script setup lang="ts">
import { CONTAINER_MAX_WIDTH } from '@/layouts/default.vue'
import type { Elemental, ElementalType, ViewSettings, ViewType } from '~~/src/types/elemental'
import ElementalGrid from '@/components/Elemental/Grid/index.vue'
import { useAdsStatusQuery } from '~~/src/composables/query/ads/getAdsStatusQuery'

const props = defineProps<Props>()

defineEmits<Emits>()

const ListFeed = defineAsyncComponent(() => import('@/components/Feed/index.vue'))

const { data, suspense } = useAdsStatusQuery()
await suspense()

interface Emits {
  (event: 'toggleView', mode: ViewType): void
  (event: 'loadMore'): void
}

interface Props {
  mode: ViewType
  elementalPreset?: ElementalType[]
  elementalTypes: ElementalTypeEnum[]
  items?: Elemental[]
  viewSettings: ViewSettings
  isLoading: boolean
  canLoadMode: boolean
}

const containerRef = ref()
const { width } = useElementSize(containerRef)
const isSmall = computed(() => width.value < 768)
const isMedium = computed(() => !isSmall.value && width.value < 1024)
const isLarge = computed(() => !isSmall.value && !isMedium.value)
const lastLength = ref(0)
const itensPerScreen = computed(() => {
  if (isSmall.value)
    return [2, 3] as const

  if (isLarge.value)
    return [4, 3] as const

  return [3, 3] as const
})
const cardsAndAds = ref<number[]>([])

const calculateAdsIndex = (lastIndex = 0) => {
  if (!data.value || !data.value['in-content'])
    return

  const ads: number[] = []
  const [x, y] = itensPerScreen.value
  const itemsPerView = (x * y)
  const size = unref(props.items)?.length
  if (!size)
    return
  for (let ad = lastIndex; ad <= size; ad += itemsPerView * 2) {
    const max = itemsPerView
    const min = 0
    const pos = (Math.floor(Math.random() * (max - min + 1)) + min) + ad
    ads.push(pos)
  }
  lastLength.value = size - 1
  cardsAndAds.value = ads
}

watch([() => props.items, itensPerScreen], () => {
  calculateAdsIndex(lastLength.value)
})

watch(data, () => {
  calculateAdsIndex(lastLength.value)
}, { deep: true })
</script>

<template>
  <main pb="62px">
    <div :style="{ maxWidth: `${CONTAINER_MAX_WIDTH}px` }" class="mx-auto w-full pb-5 relative z-2">
      <PromptFiltersVisual
        :default-layout="mode" :selected-elemental-types="elementalTypes"
        :elemental-types="elementalPreset"
        @change-layout="$emit('toggleView', $event)"
      />
    </div>

    <div
      id="main-content"
      ref="containerRef"
    >
      <ElementalGrid
        v-if="mode === 'grid'"
        :data="items"
        :card-options="viewSettings"
        :is-loading="isLoading"
        :ads-positions="cardsAndAds"
        :itens-per-view="itensPerScreen[0]"
      />

      <ListFeed
        v-if="mode === 'list'"
        :items="items"
        :is-loading="isLoading"
        :ads-positions="cardsAndAds"
        :can-load-more="canLoadMode"
        :show-empty-feed-message="!!items?.length && items.length < 1"

        @load-more="$emit('loadMore')"
      />
    </div>
  </main>
</template>
